import type { ComponentType } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';

/**
 * Inject a TanStack query's data into a (possibly class) component as a named prop.
 *
 * Universal: one factory instead of a bespoke HOC per query. The query's data is
 * injected under `propName`, falling back to `fallback` while the query is
 * loading / errored (so consumers never see `undefined`).
 *
 * @example
 *   compose(
 *     connect(...),
 *     withQueryProp(useDocumentSubtypes, 'subtypes', []),
 *   )(MyClassComponent);
 */
export const withQueryProp =
  <TData, TName extends string>(useQueryHook: () => UseQueryResult<TData>, propName: TName, fallback: TData) =>
  <P extends Record<TName, TData>>(Component: ComponentType<P>) =>
  (props: Omit<P, TName>) => {
    const { data = fallback } = useQueryHook();
    // Computed-key merge is imprecise for TS to prove equal to P; the generic
    // constraint on P guarantees the shape is correct at every call site.
    const merged = { ...props, [propName]: data } as unknown as P;
    return <Component {...merged} />;
  };

export default withQueryProp;
